
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useUser } from '@/contexts/UserContext';
import { profileService } from '@/services/profile';
import { mealPlanStatsService } from '@/services/mealPlan/mealPlanStatsService';
import { USER_PROFILE, getEntityKey } from '@/constants/queryKeys';
import { differenceInDays } from 'date-fns';
import { analytics } from '@/lib/analytics';

// New query key for total meal plans count
const TOTAL_MEAL_PLANS_COUNT = 'total-meal-plans-count';

/**
 * Hook to detect if the current user is a "new user"
 * A new user graduates from onboarding when BOTH conditions are met:
 * - Account is 5+ days old
 * - User has 3+ total meal plans
 */
export const useIsNewUser = () => {
  const { isAuthenticated, user } = useUser();
  
  // Fetch user profile to get account creation date
  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: getEntityKey(USER_PROFILE, user?.id),
    queryFn: () => profileService.getUserProfile(user!.id),
    enabled: isAuthenticated && !!user,
  });

  // Efficiently count total meal plans
  const { data: totalMealPlansCount = 0, isLoading: mealPlansCountLoading } = useQuery({
    queryKey: [TOTAL_MEAL_PLANS_COUNT, user?.id],
    queryFn: mealPlanStatsService.getTotalMealPlansCount,
    enabled: isAuthenticated && !!user,
  });

  // Calculate account age in days
  const accountAgeDays = profile?.created_at 
    ? differenceInDays(new Date(), new Date(profile.created_at))
    : 0;

  // New user logic: Graduate when account is 5+ days old AND has 3+ meal plans
  const hasMaturedAccount = accountAgeDays >= 5;
  const hasSignificantMealPlanning = totalMealPlansCount >= 3;
  const isNewUser = isAuthenticated && !(hasMaturedAccount && hasSignificantMealPlanning);

  // Track onboarding milestones
  useEffect(() => {
    if (isAuthenticated && user && profile && !profileLoading && !mealPlansCountLoading) {
      // Track onboarding start for new users
      if (isNewUser && accountAgeDays === 0) {
        analytics.track('onboarding_started', {
          user_id: user.id,
          account_age_days: accountAgeDays,
          total_meal_plans: totalMealPlansCount,
        });
      }

      // Track onboarding completion when user graduates
      if (!isNewUser && (hasMaturedAccount || hasSignificantMealPlanning)) {
        analytics.track('onboarding_completed', {
          user_id: user.id,
          account_age_days: accountAgeDays,
          total_meal_plans: totalMealPlansCount,
          graduated_by: hasMaturedAccount && hasSignificantMealPlanning ? 'both' : 
                       hasMaturedAccount ? 'account_age' : 'meal_planning',
        });
      }

      // Track milestone progress
      if (isNewUser) {
        analytics.track('onboarding_milestone_reached', {
          user_id: user.id,
          account_age_days: accountAgeDays,
          total_meal_plans: totalMealPlansCount,
          account_mature: hasMaturedAccount,
          meal_planning_significant: hasSignificantMealPlanning,
        });
      }

      // Update user properties for analytics
      analytics.identify({
        account_age_days: accountAgeDays,
        total_meal_plans: totalMealPlansCount,
        is_new_user: isNewUser,
      });
    }
  }, [isAuthenticated, user, profile, profileLoading, mealPlansCountLoading, isNewUser, 
      accountAgeDays, totalMealPlansCount, hasMaturedAccount, hasSignificantMealPlanning]);

  return {
    isNewUser,
    isLoading: (profileLoading || mealPlansCountLoading) && isAuthenticated,
    totalMealPlansCount
  };
};
